import { useContext } from 'react'
import { ChevronRightIcon, ClipboardDocumentListIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'libs/i18n'
import { Link } from 'libs/router'
import { format, parseISO } from 'libs/dates'

import { routes } from 'config/routes'
import { DashboardContext } from 'store'
import { Badge } from 'shared/atoms'
import { CardWrapper, Currency } from 'shared/components'
import { Currency as CurrencyType } from 'shared/types/general.types'
import { EmptyState } from 'shared/components/EmptyState/EmptyState'
import { DashboardCardHeader, DashboardViewAllLink } from '../molecules'
import { useBookingDisplayStatus } from 'features/bookings/hooks/useBookingDisplayStatus'

const formatDate = (date: string) => format(parseISO(date), 'dd.MM.yyyy')

export const DashboardAreasList = () => {
	const { t } = useTranslation()
	const dashboardCtx = useContext(DashboardContext)
	const currency = dashboardCtx?.dashboard?.currency as CurrencyType
	const displayStatus = useBookingDisplayStatus()

	const bookings = dashboardCtx?.dashboard?.bookings ?? []

	return (
		<CardWrapper className="flex min-w-0 flex-col">
			<DashboardCardHeader
				icon={<ClipboardDocumentListIcon />}
				iconBgColor="bg-success-soft"
				iconColor="text-success"
				label={t('Booking status')}
				title={t('Bookings')}
				aside={
					<span className="shrink-0 rounded-md bg-primary-soft px-2.5 py-1 text-xs font-semibold text-primary">
						{t('{{ count }} total', { count: bookings.length })}
					</span>
				}
			/>
			{bookings.length ? (
				<>
					<div className="hidden grid-cols-[minmax(180px,1.5fr)_90px_120px_24px] gap-4 border-b border-border bg-background px-5 py-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground sm:grid">
						<span>{t('Booking')}</span>
						<span>{t('Quantity sold')}</span>
						<span>{t('Amount sold')}</span>
						<span />
					</div>
					<div className="flex-1 divide-y divide-border">
						{bookings.map((booking) => {
							const status = displayStatus(booking.status, booking.start_date, booking.end_date)

							return (
								<Link
									key={booking.id}
									to={routes.bookingEdit(String(booking.id))}
									className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-3 px-4 py-4 transition-colors hover:bg-muted/60 sm:grid-cols-[minmax(180px,1.5fr)_90px_120px_24px] sm:items-center sm:gap-4 sm:px-5 sm:py-3">
									<span className="min-w-0">
										<span className="flex items-center gap-2">
											<Badge variant={status.variant}>{status.label}</Badge>
											{booking.booth ? (
												<span className="text-[11px] font-semibold text-muted-foreground">
													#{booking.booth.number}
												</span>
											) : null}
										</span>
										<span className="mt-2 block text-[12px] font-semibold sm:mt-1.5">
											{formatDate(booking.start_date)} – {formatDate(booking.end_date)}
										</span>
									</span>
									<ChevronRightIcon className="mt-1 h-4 w-4 shrink-0 stroke-2 text-muted-foreground sm:order-last sm:mt-0" />
									<span className="col-span-2 grid grid-cols-2 gap-3 sm:contents">
										<span>
											<span className="block text-[10px] font-medium uppercase text-muted-foreground sm:hidden">
												{t('Quantity sold')}
											</span>
											<span className="mt-0.5 block font-display text-sm font-semibold sm:mt-0">
												{status.hasStarted ? booking.sold_products : '—'}
											</span>
										</span>
										<span>
											<span className="block text-[10px] font-medium uppercase text-muted-foreground sm:hidden">
												{t('Amount sold')}
											</span>
											<span className="mt-0.5 block font-display text-sm font-semibold sm:mt-0">
												{status.hasStarted ? (
													<Currency price={{ amount: booking.amount_sold, currency }} />
												) : (
													'—'
												)}
											</span>
										</span>
									</span>
								</Link>
							)
						})}
					</div>
					<DashboardViewAllLink to={routes.bookingIndex} />
				</>
			) : (
				<EmptyState icon={<ClipboardDocumentListIcon />} text={t('No active bookings')} />
			)}
		</CardWrapper>
	)
}
